import { CALLINGS, type CallingId, crimesIn, getCalling, getItem, getTown, minigameAt, roadsFrom, shopsIn, skill, type Stat, venuesIn } from '@wyn/content';
import { attackCost, balance } from '@wyn/game-core';
import type { CrimeOutcome } from '@wyn/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { FirstSteps } from '../components/FirstSteps.tsx';
import { NoticeBoard } from '../components/NoticeBoard.tsx';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { ReportButton } from '../components/Report.tsx';
import { Callout, PageTitle } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import { UI } from '../icons/ui-icons.ts';
import { townQuery, useAction, useFight, useTrain } from '../lib/queries.ts';
import type { Tone } from '../lib/theme.ts';
import { coin, formatDuration, useNow } from '../lib/time.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

const STATS: { id: Stat; label: string; icon: string; blurb: string }[] = [
  { id: 'strength', label: 'Strength', icon: UI.strength, blurb: 'damage dealt' },
  { id: 'defence', label: 'Defence', icon: UI.defence, blurb: 'damage taken' },
  { id: 'speed', label: 'Speed', icon: UI.speed, blurb: 'hit chance, turn order' },
  { id: 'dexterity', label: 'Dexterity', icon: UI.dexterity, blurb: 'dodge, ranged aim' },
];

export function Town() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;
  const p = me.data.player;
  const town = getTown(p.town);
  const kind = p.status.kind;
  return (
    <GameShell me={me.data} skew={me.skew}>
      <div className="space-y-6">
        {kind === 'travelling' ? (
          <OnTheRoad />
        ) : (
          <>
            <section>
              <PageTitle icon={town.icon}>{town.name}</PageTitle>
              <p className="text-ink-dim">{town.description}</p>
            </section>
            <FirstSteps player={p} />
            <NoticeBoard player={p} />
            {kind === 'gaol' && <Gaol />}
            {kind === 'infirmary' && <Infirmary />}
            {kind === 'idle' && (
              <>
                <TrainingYard />
                <BackAlleys />
                <Roads />
              </>
            )}
            {p.town === 'wynchester' && kind !== 'gaol' && (
              <Link to="/bank" className="panel block hover:border-gilt">
                <div><Icon name={UI.bank} className="mr-2 text-gilt" />The Royal Bank</div>
                <div className="text-sm text-ink-dim">Deposit coin where no cutpurse can reach it, or lock it away for interest.</div>
              </Link>
            )}
            {p.town === balance.calling.town && kind === 'idle' && <HeraldsOffice />}
            {kind !== 'gaol' && <Shops />}
            {kind === 'idle' && <FolkHere />}
            <Venues />
          </>
        )}
      </div>
    </GameShell>
  );
}

function Venues() {
  const me = useMe();
  const p = me.data!.player!;
  const levels = new Map(me.data!.skills.map((s) => [s.skill, s.level]));
  return (
    <section id="venues" className="scroll-mt-4 space-y-2">
      <h2 className="heading">Venues</h2>
      <ul className="grid gap-2 sm:grid-cols-2">
        {venuesIn(p.town).map((v) => {
          const open = !!minigameAt(v.key);
          const body = (
            <>
              <div><Icon name={v.icon} className="mr-2" />{v.name}</div>
              <div className="text-sm text-ink-dim">{v.description}</div>
              <div className="pt-1 text-xs text-ink-faint">
                {open ? `${skill(v.skill).name} · Lv ${levels.get(v.skill) ?? 1}${p.minigame?.venue === v.key ? ' · game in progress' : ''}` : 'Doors open soon'}
              </div>
            </>
          );
          return (
            <li key={v.key}>
              {open ? (
                <Link to="/venue/$venueKey" params={{ venueKey: v.key }} className="panel block hover:border-gilt">{body}</Link>
              ) : (
                <div className="panel opacity-60">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Time left on the player's current timer, ticking once a second against the server clock. */
function useTimeLeft() {
  const me = useMe();
  const now = useNow();
  const s = me.data!.player!.status;
  return s.kind === 'idle' ? 0 : Math.max(0, s.endsAt - (now + me.skew));
}

function OnTheRoad() {
  const me = useMe();
  const left = useTimeLeft();
  const s = me.data!.player!.status;
  if (s.kind !== 'travelling') return null;
  const pct = Math.min(100, ((s.endsAt - s.departedAt - left) / (s.endsAt - s.departedAt)) * 100);
  return (
    <section className="panel space-y-3">
      <PageTitle icon={UI.road}>On the road</PageTitle>
      <p className="text-ink-dim">
        From {getTown(s.from).name} to {getTown(s.to).name}. Nobody can reach you out here, and there is nothing to do but walk.
      </p>
      <div className="flex items-center gap-3 font-mono text-sm">
        <Icon name={getTown(s.from).icon} className="text-lg text-ink-dim" />
        <div className="h-1.5 flex-1 bg-rule" role="progressbar" aria-label="Journey" aria-valuenow={Math.round(pct)}>
          <div className="h-full bg-gilt transition-[width] duration-1000" style={{ width: `${pct}%` }} />
        </div>
        <Icon name={getTown(s.to).icon} className="text-lg text-gilt" />
      </div>
      <p className="font-mono text-sm text-ochre">Arriving in {formatDuration(left)}</p>
    </section>
  );
}

function Gaol() {
  const me = useMe();
  const left = useTimeLeft();
  const bail = useAction('bail');
  const p = me.data!.player!;
  if (p.status.kind !== 'gaol') return null;
  return (
    <section className="panel space-y-3 border-blood/60">
      <h2 className="heading text-blood"><Icon name={UI.gaol} className="mr-1" />The Gaol</h2>
      <p>You are locked up for {p.status.reason.toLowerCase()}. Released in <span className="font-mono text-ochre">{formatDuration(left)}</span>.</p>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn" disabled={bail.isPending || p.bail === null || p.bail > p.purse} onClick={() => bail.mutate({})}>
          Pay bail · {coin(p.bail ?? 0)}
        </button>
        {p.bail !== null && p.bail > p.purse && <span className="text-sm text-ink-faint">Your purse is too light.</span>}
      </div>
      <p className="text-xs text-ink-faint">Bail falls as your sentence runs down.</p>
      <ErrorNotice error={bail.error} />
    </section>
  );
}

function Infirmary() {
  const me = useMe();
  const left = useTimeLeft();
  const drink = useAction('useItem', { refetch: ['me'] });
  const p = me.data!.player!;
  const draughts = me.data!.inventory.filter((i) => getItem(i.itemKey).use?.infirmaryMinutes);
  if (p.status.kind !== 'infirmary') return null;
  return (
    <section className="panel space-y-3 border-ochre/60">
      <h2 className="heading text-ochre"><Icon name={UI.infirmary} className="mr-1" />The Infirmary</h2>
      <p>Laid up after {p.status.reason.toLowerCase()}. Discharged in <span className="font-mono text-ochre">{formatDuration(left)}</span>.</p>
      {draughts.length === 0 ? (
        <p className="text-sm text-ink-faint">You have nothing to drink. The Apothecary sells healing draughts.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {draughts.map((d) => (
            <button key={`${d.itemKey}#${d.quality}`} type="button" className="btn" disabled={drink.isPending} onClick={() => drink.mutate({ itemKey: d.itemKey, quality: d.quality })}>
              Drink a {getItem(d.itemKey).name.toLowerCase()} (×{d.quantity})
            </button>
          ))}
        </div>
      )}
      {drink.data && !drink.isPending && (
        <Callout tone="good">
          +{drink.data.outcome.lifeRestored} Life{drink.data.outcome.discharged ? '. You are free to go.' : `, ${drink.data.outcome.minutesOff} minutes off your stay.`}
        </Callout>
      )}
      <ErrorNotice error={drink.error} />
    </section>
  );
}

const RESULT_TONE: Record<CrimeOutcome['result'], Tone> = {
  success: 'good',
  failure: 'muted',
  caught: 'bad',
  mishap: 'warn',
};

function BackAlleys() {
  const me = useMe();
  const crime = useAction('crime');
  const p = me.data!.player!;
  const nerve = Math.floor(p.bars.nerve.value);
  const last = crime.data?.outcome;
  return (
    <section id="back-alleys" className="panel scroll-mt-4 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading">Back alleys</h2>
        <span className="text-sm text-ink-faint">Petty crime spends Nerve. Get caught and it is the Gaol.</span>
      </div>
      <ul className="space-y-2">
        {crimesIn(p.town).map((c) => (
          <li key={c.key} className="flex flex-wrap items-center justify-between gap-2 border-b border-rule pb-2 last:border-0 last:pb-0">
            <div className="min-w-0">
              <div><Icon name={c.icon} className="mr-2" />{c.name}</div>
              <div className="text-xs text-ink-faint">{c.description} · up to {c.coin.max} coin</div>
            </div>
            <button type="button" className="btn" disabled={crime.isPending || nerve < c.nerve} onClick={() => crime.mutate({ crime: c.key })} aria-label={c.name}>
              <Icon name={UI.nerve} className="mr-1" />{c.nerve}
            </button>
          </li>
        ))}
      </ul>
      {last && !crime.isPending && (
        <Callout tone={RESULT_TONE[last.result]} role="status">
          {last.text}
          {last.renown > 0 && <span className="text-ink-faint"> +{last.renown} Renown.</span>}
        </Callout>
      )}
      <ErrorNotice error={crime.error} />
    </section>
  );
}

function Roads() {
  const me = useMe();
  const go = useAction('travel', { refetch: ['town'] });
  const p = me.data!.player!;
  return (
    <section id="roads" className="panel scroll-mt-4 space-y-3">
      <h2 className="heading">Roads</h2>
      <ul className="grid gap-2 sm:grid-cols-3">
        {roadsFrom(p.town).map((r) => {
          const t = getTown(r.to);
          return (
            <li key={r.to}>
              <button type="button" className="w-full border border-rule px-3 py-2 text-left hover:border-gilt disabled:opacity-40" disabled={go.isPending} onClick={() => go.mutate({ to: r.to })}>
                <div><Icon name={t.icon} className="mr-1 text-gilt" />To {t.name}</div>
                <div className="font-mono text-xs text-ink-faint">{r.minutes} minutes on foot</div>
              </button>
            </li>
          );
        })}
      </ul>
      <ErrorNotice error={go.error} />
    </section>
  );
}

/** Wynchester's Herald records a change of Calling: once every 30 days, for a fee. */
function HeraldsOffice() {
  const me = useMe();
  const now = useNow();
  const change = useAction('changeCalling');
  const p = me.data!.player!;
  const [to, setTo] = useState<CallingId | ''>('');
  const wait = p.callingChange.at - (now + me.skew);
  const { fee } = p.callingChange;
  return (
    <section className="panel space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading"><Icon name={UI.herald} className="mr-1" />The Herald’s Office</h2>
        <span className="text-sm text-ink-faint">Change your Calling once every {balance.calling.cooldownDays} days. Skills and titles stay; the kit is not given again.</span>
      </div>
      {wait > 0 ? (
        <p className="text-sm">You are written in the roll as a {getCalling(p.calling).name}. The Herald will not change it again for <span className="font-mono text-ochre">{formatDuration(wait)}</span>.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select className="field w-48" aria-label="New Calling" value={to} onChange={(e) => setTo(e.target.value as CallingId)}>
            <option value="">— choose a Calling —</option>
            {CALLINGS.filter((c) => c !== p.calling).map((c) => (
              <option key={c} value={c}>{getCalling(c).name}: {getCalling(c).perk.name}</option>
            ))}
          </select>
          <button type="button" className="btn" disabled={!to || change.isPending || p.purse < fee} onClick={() => to && change.mutate({ calling: to }, { onSuccess: () => setTo('') })}>
            Change Calling · {coin(fee)}
          </button>
          {to && <span className="text-xs text-ink-faint">{getCalling(to).perk.description} Affinity: {getCalling(to).affinity.map((s) => skill(s).name).join(', ')}.</span>}
        </div>
      )}
      {change.data && !change.isPending && <Callout tone="good">You are now a {getCalling(change.data.outcome.to).name}.</Callout>}
      <ErrorNotice error={change.error} />
    </section>
  );
}

function Shops() {
  const me = useMe();
  const buy = useAction('buy', { refetch: ['me'] });
  const p = me.data!.player!;
  return (
    <div id="shops" className="scroll-mt-4 space-y-6">
      {shopsIn(p.town).map((shop) => (
        <section key={shop.key} className="panel space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="heading"><Icon name={shop.icon} className="mr-1" />{shop.name}</h2>
            <span className="text-sm text-ink-faint">{shop.description}</span>
          </div>
          <ul className="space-y-2">
            {shop.stock.map((line) => {
              const item = getItem(line.item);
              const owned = me.data!.inventory.filter((i) => i.itemKey === line.item).reduce((n, i) => n + i.quantity, 0);
              return (
                <li key={line.item} className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Icon name={item.icon} className="mr-2" />{item.name}
                    <span className="ml-2 text-xs text-ink-faint">{item.description} You have {owned}.</span>
                  </div>
                  <button type="button" className="btn" disabled={buy.isPending || p.purse < line.price} onClick={() => buy.mutate({ shop: shop.key, itemKey: line.item, quantity: 1 })}>
                    Buy · {coin(line.price)}
                  </button>
                </li>
              );
            })}
          </ul>
          <ErrorNotice error={buy.error} />
        </section>
      ))}
    </div>
  );
}

function FolkHere() {
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  // Ask the server once typing pauses, not on every key.
  useEffect(() => {
    const id = setTimeout(() => setName(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const q = useQuery(townQuery(name));
  const me = useMe();
  const { attack } = useFight();
  const navigate = useNavigate();
  const p = me.data!.player!;
  const others = (q.data?.people ?? []).filter((x) => x.id !== p.id);
  const total = q.data?.total ?? 0;
  // Only a town too busy to list everyone needs searching.
  const searchable = !!search || total > others.length;
  const cost = attackCost(p);
  const tired = p.bars.vigour.value < cost;
  // Arrivals write nothing to push, so look again when the next traveller is due.
  const qc = useQueryClient();
  const arrival = q.data?.nextArrivalAt ?? null;
  useEffect(() => {
    if (arrival === null) return;
    const id = setTimeout(() => void qc.invalidateQueries({ queryKey: ['town'] }), Math.max(1_000, arrival - me.skew - Date.now() + 500));
    return () => clearTimeout(id);
  }, [arrival, me.skew, qc]);
  return (
    <section id="folk" className="panel scroll-mt-4 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading">Folk about town</h2>
        <span className="text-sm text-ink-faint">An attack costs {cost} Vigour.</span>
      </div>
      {searchable && (
        <div className="space-y-1">
          <input className="field w-full" aria-label="Find someone by name" placeholder="Find someone by name" maxLength={40} value={search} onChange={(e) => setSearch(e.target.value)} />
          {total > others.length && (
            <p className="text-xs text-ink-faint">
              Showing the {others.length} newest arrivals of {total.toLocaleString('en-GB')}{name ? ' by that name' : ''}.
            </p>
          )}
        </div>
      )}
      {others.length === 0 ? (
        <p className="text-sm text-ink-faint">{name ? 'Nobody here by that name.' : 'Nobody you know.'}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {others.map((x) => (
            <li key={x.id} className="flex items-center justify-between gap-2">
              <span>
                {x.name}
                {x.resident && (
                  <span className="ml-1.5 text-xs text-ink-faint" title="A resident: one of the simulated folk who keep the realm lively until it fills with players.">
                    · resident
                  </span>
                )}
              </span>
              <span className="flex items-center gap-3">
                <ReportButton target={{ kind: 'player', name: x.name }} what={x.name} />
                <button
                  type="button"
                  className="btn-quiet py-0"
                  aria-label={`Attack ${x.name}`}
                  disabled={attack.isPending || tired || !!p.fight}
                  onClick={() => attack.mutate(x.id, { onSuccess: () => void navigate({ to: '/fight' }) })}
                >
                  <Icon name={UI.attack} className="mr-1" />attack
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <ErrorNotice error={attack.error} />
    </section>
  );
}

function TrainingYard() {
  const me = useMe();
  const trainM = useTrain();
  const [stat, setStat] = useState<Stat>('strength');
  const [amount, setAmount] = useState(10);
  const p = me.data!.player!;
  const yardStat = getTown(p.town).yardStat;
  const vigour = Math.floor(p.bars.vigour.value);
  const blocked = p.status.kind !== 'idle';
  const last = trainM.data?.outcome;

  return (
    <section id="training-yard" className="panel scroll-mt-4 space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading">Training Yard</h2>
        <span className="text-sm text-ink-faint">This yard favours {STATS.find((s) => s.id === yardStat)!.label} (+25%)</span>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {STATS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setStat(s.id)}
            aria-pressed={stat === s.id}
            className={`border px-3 py-2 text-left transition-colors ${stat === s.id ? 'border-gilt text-ink' : 'border-rule text-ink-dim hover:border-ink-faint'}`}
          >
            <div><Icon name={s.icon} className="mr-1" />{s.label}{s.id === yardStat && <Icon name={UI.favoured} className="ml-1 text-gilt" />}</div>
            <div className="font-mono text-sm tabular-nums">{p.stats[s.id].toLocaleString('en-GB', { maximumFractionDigits: 2 })}</div>
            <div className="text-xs text-ink-faint">{s.blurb}</div>
          </button>
        ))}
      </div>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          trainM.mutate({ stat, vigour: amount });
        }}
      >
        <label className="flex items-center gap-2">
          <span className="text-sm text-ink-dim">Spend</span>
          <input
            className="field w-20 font-mono"
            type="number"
            min={1}
            max={Math.max(1, vigour)}
            value={amount}
            onChange={(e) => setAmount(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
          />
          <span className="text-sm text-ink-dim">Vigour</span>
        </label>
        {[5, 25].map((n) => (
          <button key={n} type="button" className="btn-quiet" onClick={() => setAmount(n)}>{n}</button>
        ))}
        <button type="button" className="btn-quiet" onClick={() => setAmount(Math.max(1, vigour))}>all</button>
        <button className="btn" type="submit" disabled={blocked || trainM.isPending || amount > vigour}>
          {trainM.isPending ? 'Training…' : 'Train'}
        </button>
      </form>
      <p className="text-xs text-ink-faint">Gains grow with Mood. Training spends Mood equal to half the Vigour.</p>
      {last && !trainM.isPending && (
        <Callout tone="good">
          You drill {last.stat} until your arms ache: <span className="text-verdigris">+{last.gain.toLocaleString('en-GB')}</span> (now{' '}
          {last.newValue.toLocaleString('en-GB')}), and +{last.renown} Renown. −{last.vigourSpent} Vigour, −{last.moodSpent} Mood.
        </Callout>
      )}
      <ErrorNotice error={trainM.error} />
    </section>
  );
}
